import { useChecklistDraft } from './useChecklistDraft';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Textarea,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  Chip,
  Input,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBack,
  CheckCircle,
  SkipForward as SkipNext,
  CaretRight as NavigateNext,
  CaretLeft as NavigateBefore,
  SignOut as ExitToApp,
  LinkSimple as LinkIcon
} from '@phosphor-icons/react';
import { ChecklistsAPI } from '../../lib/apiChecklists';
import { ChecklistStepPhotos } from '../../components/checklists/ChecklistStepPhotos';
import { ChecklistStepPhotoUploadModal } from '../../components/checklists/ChecklistStepPhotoUploadModal';
import { useChecklistStepPhotos } from '../../hooks/useChecklistStepPhotos';
import type { ChecklistRun, UpdateChecklistStepRunRequest, SkipStepRequest } from '../../types/checklists';

const ChecklistRunExecutionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [checklistRun, setChecklistRun] = useState<ChecklistRun | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  // Get photos for current step
  const currentStepRun = checklistRun?.step_runs[currentStepIndex];
  const { notes, skipReason, setNotes, setSkipReason, saveState } = useChecklistDraft(currentStepRun, checklistRun?.status === 'in_progress');
  const { 
    data: stepPhotos = [], 
    isLoading: photosLoading, 
    error: photosError, 
    refetch: refetchPhotos 
  } = useChecklistStepPhotos(currentStepRun?.id);


  const handlePhotoUploadSuccess = () => {
    refetchPhotos();
    setShowPhotoUpload(false);
  };

  const load = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const data = await ChecklistsAPI.retrieveRun(parseInt(id));
      setChecklistRun(data);
      
      // Find the first incomplete step
      const firstIncompleteIndex = data.step_runs.findIndex(
        step => step.status === 'pending'
      );
      if (firstIncompleteIndex !== -1) {
        setCurrentStepIndex(firstIncompleteIndex);
      }
    } catch (error) {
      console.error('Failed to load checklist run:', error);
      setError('Failed to load checklist run');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteStep = async () => {
    if (!checklistRun || currentStepIndex >= checklistRun.step_runs.length) return;

    const currentStep = checklistRun.step_runs[currentStepIndex];
    
    // Check if photos are required and none have been uploaded
    if (currentStep.step_photos_required && stepPhotos.length === 0) {
      setError('Photos are required for this step. Please upload at least one photo before completing.');
      return;
    }
    
    setUpdating(true);
    setError(null);
    
    try {
      const updateData: UpdateChecklistStepRunRequest = {
        status: 'completed',
        notes: notes.trim(),
      };

      await ChecklistsAPI.updateStepRun(currentStep.id, updateData);
      await load(); // Reload to get updated data

      
      // Move to next incomplete step
      const nextIncompleteIndex = checklistRun.step_runs.findIndex(
        (step, index) => index > currentStepIndex && step.status === 'pending'
      );
      
      if (nextIncompleteIndex !== -1) {
        setCurrentStepIndex(nextIncompleteIndex);
      } else if (currentStepIndex < checklistRun.step_runs.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } catch (error) {
      console.error('Failed to complete step:', error);
      setError('Failed to complete step');
    } finally {
      setUpdating(false);
    }
  };

  const handleSkipStep = async () => {
    if (!checklistRun || currentStepIndex >= checklistRun.step_runs.length) return;
    if (!skipReason.trim()) {
      setError('Please provide a reason for skipping this step');
      return;
    }

    const currentStep = checklistRun.step_runs[currentStepIndex];
    
    setUpdating(true);
    setError(null);
    
    try {
      const skipData: SkipStepRequest = {
        skip_reason: skipReason.trim(),
        notes: notes.trim(),
      };

      await ChecklistsAPI.skipStepRun(currentStep.id, skipData);
      await load(); // Reload to get updated data


      
      // Move to next incomplete step
      const nextIncompleteIndex = checklistRun.step_runs.findIndex(
        (step, index) => index > currentStepIndex && step.status === 'pending'
      );
      
      if (nextIncompleteIndex !== -1) {
        setCurrentStepIndex(nextIncompleteIndex);
      } else if (currentStepIndex < checklistRun.step_runs.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } catch (error) {
      console.error('Failed to skip step:', error);
      setError('Failed to skip step');
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteChecklist = async () => {
    if (!checklistRun) return;

    setUpdating(true);
    try {
      await ChecklistsAPI.completeRun(checklistRun.id);
      navigate('/checklists');
    } catch (error) {
      console.error('Failed to complete checklist:', error);
      setError('Failed to complete checklist');
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography level="body-sm">Loading checklist run...</Typography>
      </Box>
    );
  }

  if (!checklistRun) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography level="body-sm">Checklist run not found</Typography>
        <Button onClick={() => navigate('/checklists')} sx={{ mt: 2 }}>
          Back to Checklists
        </Button>
      </Box>
    );
  }

  const currentStep = checklistRun?.step_runs[currentStepIndex];
  if (!currentStep) return <Alert>No steps are available for this execution. <Button onClick={() => navigate('/checklists')}>Back</Button></Alert>;
  const canGoNext = currentStepIndex < (checklistRun?.step_runs.length ?? 0) - 1;
  const canGoPrevious = currentStepIndex > 0;
  const allStepsComplete = checklistRun?.step_runs.every(step => step.status !== 'pending') ?? false;

  return (
    <Box sx={{ p: 2, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/checklists')} variant="plain">
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography level="h3">{checklistRun.checklist_title}</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            {checklistRun.job_name ? `Job: ${checklistRun.job_name}` : `Opportunity: ${checklistRun.opportunity_name}`}
          </Typography>
        </Box>
        <Chip 
          color={checklistRun.status === 'completed' ? 'success' : 'primary'}
          variant="soft"
        >
          {checklistRun.status === 'completed' ? 'Completed' : `${checklistRun.progress_percentage}% Complete`}
        </Chip>
      </Box>

      <Typography level="body-sm" sx={{ mb: 1 }} aria-live="polite">Revision {checklistRun.revision || 1} · {saveState}</Typography>
      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography level="body-sm">
            Step {currentStepIndex + 1} of {checklistRun.step_runs.length}
          </Typography>
          <Typography level="body-sm">
            {checklistRun.completed_steps} / {checklistRun.total_steps} completed
          </Typography>
        </Box>
        <LinearProgress 
          determinate 
          value={checklistRun.progress_percentage} 
          sx={{ height: 8 }}
        />
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Current Step */}
      {currentStep && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {currentStep.status === 'completed' ? (
            <CheckCircle color="var(--joy-palette-success-500)" />
          ) : currentStep.status === 'skipped' ? (
            <SkipNext color="var(--joy-palette-warning-500)" />
          ) : (
            <Box sx={{ 
              width: 24, 
              height: 24, 
              borderRadius: '50%', 
              border: 2, 
              borderColor: 'primary.500' 
            }} />
          )}
          <Typography level="h4" sx={{ flex: 1 }}>
            {currentStep.step_title}
          </Typography>
          <Chip 
            size="sm" 
            color={
              currentStep.status === 'completed' ? 'success' : 
              currentStep.status === 'skipped' ? 'warning' : 'neutral'
            }
          >
            {currentStep.status}
          </Chip>
        </Box>

        {currentStep.step_description && (
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
            {currentStep.step_description}
          </Typography>
        )}

        {currentStep.step_reference_link && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<LinkIcon />}
              onClick={() => window.open(currentStep.step_reference_link, '_blank')}
            >
              Reference Link
            </Button>
          </Box>
        )}

        {/* Photo Upload Section */}
        {currentStep.step_photos_required && (
          <Box sx={{ mb: 2 }}>
            <ChecklistStepPhotos
          readOnly={checklistRun.status !== 'in_progress'}
              stepRunId={currentStep.id}
              photosRequired={true}
              photos={stepPhotos}
              loading={photosLoading}
              error={photosError?.message || null}
              onUploadClick={() => setShowPhotoUpload(true)}
            />
          </Box>
        )}

        {currentStep.status === 'skipped' && currentStep.skip_reason && (
          <Alert color="warning" sx={{ mb: 2 }}>
            Skipped: {currentStep.skip_reason}
          </Alert>
        )}

        {currentStep.notes && (
          <Alert color="neutral" sx={{ mb: 2 }}>
            Notes: {currentStep.notes}
          </Alert>
        )}

        {/* Actions for pending steps */}
        {currentStep.status === 'pending' && checklistRun.status === 'in_progress' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel>Notes (Optional)</FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about completing this step..."
                minRows={2}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                onClick={handleCompleteStep}
                loading={updating}
                startDecorator={<CheckCircle />}
                disabled={updating}
              >
                Complete Step
              </Button>

              {currentStep.step_allow_skip && (
                <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                  <Input
                    value={skipReason}
                    onChange={(e) => setSkipReason(e.target.value)}
                    placeholder="Reason for skipping..."
                    sx={{ flex: 1 }}
                  />
                  <Button
                    onClick={handleSkipStep}
                    loading={updating}
                    variant="outlined"
                    color="warning"
                    startDecorator={<SkipNext />}
                    disabled={updating || !skipReason.trim()}
                  >
                    Skip
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
        </Card>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<NavigateBefore />}
            onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
            disabled={!canGoPrevious}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            endDecorator={<NavigateNext />}
            onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
            disabled={!canGoNext}
          >
            Next
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {allStepsComplete && checklistRun.status === 'in_progress' && (
            <Button
              onClick={handleCompleteChecklist}
              loading={updating}
              color="success"
              startDecorator={<CheckCircle />}
            >
              Complete Checklist
            </Button>
          )}
          
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<ExitToApp />}
            onClick={() => navigate('/checklists')}
          >
            Exit
          </Button>
        </Box>
      </Box>

      {/* Steps Overview */}
      <Card sx={{ p: 2, mt: 3 }}>
        <Typography level="title-md" sx={{ mb: 2 }}>Steps Overview</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {checklistRun.step_runs.map((step, index) => (
            <Box
              key={step.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: 'sm',
                bgcolor: index === currentStepIndex ? 'primary.50' : 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'neutral.50',
                },
              }}
              aria-label={`Go to step ${index + 1}: ${step.step_title}`}
                onClick={() => setCurrentStepIndex(index)}
            >
              {step.status === 'completed' ? (
                <CheckCircle color="var(--joy-palette-success-500)" size={18} />
              ) : step.status === 'skipped' ? (
                <SkipNext color="var(--joy-palette-warning-500)" size={18} />
              ) : (
                <Box sx={{ 
                  width: 16, 
                  height: 16, 
                  borderRadius: '50%', 
                  border: 1, 
                  borderColor: index === currentStepIndex ? 'primary.500' : 'neutral.300' 
                }} />
              )}
              <Typography 
                level="body-sm" 
                sx={{ 
                  fontWeight: index === currentStepIndex ? 'bold' : 'normal',
                  color: index === currentStepIndex ? 'primary.500' : 'text.primary',
                }}
              >
                {step.step_title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      {/* Photo Upload Modal */}
      <ChecklistStepPhotoUploadModal
        open={showPhotoUpload}
        onClose={() => setShowPhotoUpload(false)}
        stepRunId={currentStepRun?.id || 0}
        jobId={checklistRun?.job}
        opportunityId={checklistRun?.opportunity}
        onUploadSuccess={handlePhotoUploadSuccess}
      />
    </Box>
  );
};

export default ChecklistRunExecutionPage;